/**
 * The lightning-strike symbol layer, as plain maplibre style-spec objects:
 * the webapp renders it as a react-map-gl `<Layer>`, and MapLibre Native can
 * take it as it is (#336 MR2c, moved out of the webapp's
 * `LightningOverlay`/`boltSprite.ts` with no change to what maplibre gets).
 *
 * maplibre can only recolour an icon per feature through an SDF (one
 * channel), which cannot carry both the bolt's white halo and its dark edge.
 * So each platform pre-renders one full-colour bolt sprite per age bucket
 * along the shared ramp ({@link LIGHTNING_BOLT_BUCKETS}: the webapp draws
 * them on a canvas, mobile rasterises {@link lightningBoltSvg} at build
 * time), and the layer picks one with a `step` expression on the feature's
 * `ageMin`. Size and opacity stay continuous (`interpolate`); only the fill
 * colour is quantised, which is imperceptible at glyph size and across the
 * one-minute age tick.
 *
 * Pure: no DOM, no canvas, no maplibre import.
 */
import type {LightningStrike} from '../Blitzortung';
import {
  LIGHTNING_AGE_WINDOW_MIN,
  LIGHTNING_BOLT_FRESH_PX,
  LIGHTNING_BOLT_POINTS,
  LIGHTNING_BOLT_VIEWBOX,
  LIGHTNING_EDGE_WIDTH,
  LIGHTNING_HALO_COLOR,
  LIGHTNING_HALO_WIDTH,
  LIGHTNING_STROKE_COLOR,
  lightningAgeColor,
  lightningScaleInterpolate,
} from './ageScale';

/** The layer's id, and its GeoJSON source's. */
export const LIGHTNING_LAYER_ID = 'lightning-strikes';

/** Age step (minutes) between pre-rendered bolt sprites. */
export const LIGHTNING_BOLT_BUCKET_STEP_MIN = 2;

export type LightningBoltBucket = {
  /** Bucket age in minutes — also the colour-ramp sample point. */
  ageMin: number;
  /** maplibre image name for this bucket. */
  name: string;
  /** Age-ramp fill colour for this bucket. */
  color: string;
};

/** One sprite per age bucket (0, 2, …, 20 min), coloured by the shared ramp. */
export const LIGHTNING_BOLT_BUCKETS: readonly LightningBoltBucket[] = (() => {
  const buckets: LightningBoltBucket[] = [];
  for (
    let ageMin = 0;
    ageMin <= LIGHTNING_AGE_WINDOW_MIN;
    ageMin += LIGHTNING_BOLT_BUCKET_STEP_MIN
  ) {
    buckets.push({
      ageMin,
      name: `lightning-bolt-${ageMin}`,
      color: lightningAgeColor(ageMin),
    });
  }
  return buckets;
})();

/**
 * maplibre `step` expression mapping a feature's `ageMin` to the nearest
 * bucket sprite (boundaries sit at the bucket midpoints).
 */
export const lightningIconImageExpression = (ageProp = 'ageMin'): unknown[] => {
  const expr: unknown[] = [
    'step',
    ['get', ageProp],
    LIGHTNING_BOLT_BUCKETS[0].name,
  ];
  for (let i = 1; i < LIGHTNING_BOLT_BUCKETS.length; i += 1) {
    const boundary =
      LIGHTNING_BOLT_BUCKETS[i].ageMin - LIGHTNING_BOLT_BUCKET_STEP_MIN / 2;
    expr.push(boundary, LIGHTNING_BOLT_BUCKETS[i].name);
  }
  return expr;
};

export type LightningLayerSpec = {
  id: string;
  type: 'symbol';
  source: string;
  layout: Record<string, unknown>;
  paint: Record<string, unknown>;
};

/**
 * The strikes' symbol layer: a bolt per strike, centred on it, every one
 * drawn (no collision culling), fresh ones largest and opaque, fading and
 * shrinking over {@link LIGHTNING_AGE_WINDOW_MIN}. A new object every call.
 */
export const lightningLayerSpec = ({
  id = LIGHTNING_LAYER_ID,
  source = LIGHTNING_LAYER_ID,
}: {id?: string; source?: string} = {}): LightningLayerSpec => ({
  id,
  type: 'symbol',
  source,
  layout: {
    'icon-image': lightningIconImageExpression('ageMin'),
    // Fresh strikes full size, shrinking with age (continuous).
    'icon-size': lightningScaleInterpolate('ageMin'),
    // Show every strike centred on its position — no collision culling.
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-anchor': 'center',
  },
  paint: {
    // Fresh strikes fully opaque, fading with age.
    'icon-opacity': [
      'interpolate',
      ['linear'],
      ['get', 'ageMin'],
      0,
      1,
      LIGHTNING_AGE_WINDOW_MIN,
      0.4,
    ],
  },
});

const MS_PER_MIN = 60 * 1000;

/** A strike's age in minutes at `now`, clamped to the visible window. */
export const lightningStrikeAgeMin = (timeMs: number, now: number): number =>
  Math.max(0, Math.min(LIGHTNING_AGE_WINDOW_MIN, (now - timeMs) / MS_PER_MIN));

/** A GeoJSON point per strike, structurally a `geojson` FeatureCollection. */
export type LightningStrikeFeatureCollection = {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    geometry: {type: 'Point'; coordinates: [number, number]};
    properties: {ageMin: number};
  }>;
};

/**
 * The layer's source data: one point per strike carrying its age at `now`.
 * Callers recompute it on a clock tick so strikes keep fading when no new
 * ones arrive.
 */
export const lightningStrikeFeatures = (
  strikes: readonly LightningStrike[],
  now: number,
): LightningStrikeFeatureCollection => ({
  type: 'FeatureCollection',
  features: strikes.map((strike) => ({
    type: 'Feature',
    geometry: {type: 'Point', coordinates: [strike.lon, strike.lat]},
    properties: {ageMin: lightningStrikeAgeMin(strike.time, now)},
  })),
});

// #rgb, #rgba, #rrggbb or #rrggbbaa (the buckets are chroma `.hex()`):
// nothing that can close the attribute it is written into.
const HEX_COLOUR = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/**
 * The bolt as a standalone SVG document, painted like the webapp's canvas
 * sprite: white halo, then the dark edge over it, then the age fill on top,
 * so the fill sits inside a thin dark rim inside a white halo (legible on
 * any basemap). For build-time rasterising (mobile's sprite script).
 */
export const lightningBoltSvg = (
  fillColor: string,
  sizePx: number = LIGHTNING_BOLT_FRESH_PX,
): string => {
  if (!HEX_COLOUR.test(fillColor)) {
    throw new Error(`lightningBoltSvg: not a hex colour: ${fillColor}`);
  }
  if (!Number.isFinite(sizePx) || sizePx <= 0) {
    throw new Error(
      `lightningBoltSvg: size must be a positive number: ${sizePx}`,
    );
  }
  const stroke = (color: string, width: number) =>
    `<polygon points="${LIGHTNING_BOLT_POINTS}" fill="none" stroke="${color}" stroke-width="${width}" stroke-linejoin="round" stroke-linecap="round"/>`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${sizePx}" height="${sizePx}" viewBox="${LIGHTNING_BOLT_VIEWBOX}">` +
    stroke(LIGHTNING_HALO_COLOR, LIGHTNING_HALO_WIDTH) +
    stroke(LIGHTNING_STROKE_COLOR, LIGHTNING_EDGE_WIDTH) +
    `<polygon points="${LIGHTNING_BOLT_POINTS}" fill="${fillColor}"/>` +
    '</svg>'
  );
};
